import { AuditTask, FIELD_TYPE, FormConfigField } from "./types";

export function byPriorityThenAge(a: AuditTask, b: AuditTask): number {
  if ((b.priority ?? 0) !== (a.priority ?? 0)) return (b.priority ?? 0) - (a.priority ?? 0);
  return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
}

// ----------------------------  "Other" correction values  ----------------------------
// With allow_other, a dropdown correction can be "Other" plus free text, stored in
// issue_category as "Other: <text>" (or just "Other" while the text is empty).
// Multi-select values are joined with MULTI_VALUE_SEPARATOR.

export const OTHER_LABEL = "Other";
export const OTHER_PREFIX = "Other: ";
export const MULTI_VALUE_SEPARATOR = "||";

export const isOtherOption = (option: string): boolean =>
  option.trim().toLowerCase() === OTHER_LABEL.toLowerCase();

export const isOtherValue = (value: string): boolean =>
  value === OTHER_LABEL || value.startsWith(OTHER_PREFIX);

export const otherText = (value: string): string =>
  value.startsWith(OTHER_PREFIX) ? value.slice(OTHER_PREFIX.length) : "";

export const toOtherValue = (text: string): string => (text ? OTHER_PREFIX + text : OTHER_LABEL);

/** Configured options to render; an explicit "Other" option is replaced by the built-in one. */
export const correctionOptions = (field: FormConfigField): string[] =>
  field.options.filter(Boolean).filter((o) => !(field.allow_other && isOtherOption(o)));

/** A correction is complete when set and any picked "Other" has text. */
export const hasCorrectionValue = (field: FormConfigField, value?: string): boolean => {
  if (!value) return false;
  if (!field.allow_other) return true;
  const parts =
    field.type === FIELD_TYPE.DROPDOWN_MULTI ? value.split(MULTI_VALUE_SEPARATOR) : [value];
  return parts.every((p) => !isOtherValue(p) || otherText(p).trim() !== "");
};

// ----------------------------  Correction details  ----------------------------

/** Dropdown fields collect a Details note alongside the picked Issue Category. */
export const isOptionField = (field: FormConfigField): boolean =>
  field.type === FIELD_TYPE.DROPDOWN_SINGLE || field.type === FIELD_TYPE.DROPDOWN_MULTI;

/**
 * Details is required once an Issue Category is picked on a dropdown field
 * (the Details box only appears then; a missing category is reported separately).
 */
export const isCorrectionNoteMissing = (
  field: FormConfigField,
  value?: string,
  note?: string,
): boolean => isOptionField(field) && !!value && !(note || "").trim();
